"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Box,
  Alert,
  Divider,
  CircularProgress,
  Checkbox,
  FormGroup,
} from "@mui/material"
import { useNotification } from "../../contexts/NotificationContext"
import { cancelSubscription } from "../../services/subscription.service"
import { formatCurrency, formatDate } from "../../utils/formatters"

const SubscriptionCancelDialog = ({ open, onClose, subscription, onCancelled }) => {
  const { showNotification } = useNotification()
  const [reason, setReason] = useState("")
  const [cancelType, setCancelType] = useState("end_of_cycle")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [confirmUnderstanding, setConfirmUnderstanding] = useState(false)
  const [commonReasons, setCommonReasons] = useState([])

  // Calculate refund amount if applicable (for immediate cancellations)
  const calculatePotentialRefund = () => {
    if (!subscription || !subscription.price) return 0

    const now = new Date()
    const endDate = new Date(subscription.currentPeriodEnd)
    const startDate = new Date(subscription.currentPeriodStart)
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))

    if (daysLeft <= 0) return 0

    const refundPercentage = daysLeft / totalDays
    return subscription.price * refundPercentage
  }

  // Populate common cancellation reasons based on plan type
  useEffect(() => {
    if (!subscription || !subscription.planId) return

    // Set common reasons based on plan type or price point
    const reasons = ["Too expensive", "Not using it enough", "Found a better alternative", "Missing features I need"]

    // Add plan-specific reasons
    if (
      subscription.planId.name.toLowerCase().includes("pro") ||
      subscription.planId.name.toLowerCase().includes("premium")
    ) {
      reasons.push("Downgrading to a lower tier")
    }

    setCommonReasons(reasons)
  }, [subscription])

  const handleReasonChange = (e) => {
    setReason(e.target.value)
  }

  const selectCommonReason = (selectedReason) => {
    setReason(reason ? `${reason}\n${selectedReason}` : selectedReason)
  }

  const handleCancelTypeChange = (e) => {
    setCancelType(e.target.value)
    // Reset confirmation when changing cancel type
    setConfirmUnderstanding(false)
  }

  const handleCancel = async () => {
    // Validate inputs
    if (!reason.trim()) {
      setError("Please provide a reason for cancellation")
      return
    }

    if (cancelType === "immediate" && !confirmUnderstanding) {
      setError("Please confirm that you understand the implications of immediate cancellation")
      return
    }

    setSubmitting(true)
    setError("")

    try {
      await cancelSubscription(subscription._id, reason, cancelType)

      showNotification(
        cancelType === "immediate"
          ? "Subscription cancelled immediately"
          : "Subscription will be cancelled at the end of the billing cycle",
        "success",
      )

      if (onCancelled) {
        onCancelled()
      }

      onClose()
    } catch (error) {
      console.error("Failed to cancel subscription:", error)
      setError(error.response?.data?.message || "Failed to cancel subscription")
      showNotification("Failed to cancel subscription", "error")
    } finally {
      setSubmitting(false)
    }
  }

  if (!subscription) return null

  const potentialRefund = calculatePotentialRefund()
  const formattedEndDate = subscription?.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : "N/A"

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" fontWeight="bold">
          Cancel Subscription
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          You're about to cancel your subscription to the <strong>{subscription?.planId?.name}</strong> plan (
          {formatCurrency(subscription?.price || 0, subscription?.currency || "USD")}/{subscription?.billingCycle}).
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            When should this cancellation take effect?
          </Typography>
          <RadioGroup value={cancelType} onChange={handleCancelTypeChange}>
            <FormControlLabel
              value="end_of_cycle"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    At the end of billing cycle
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You'll keep access until {formattedEndDate}
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="immediate"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Immediately
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    You'll lose access right away
                    {potentialRefund > 0 &&
                      subscription.billingCycle !== "free" &&
                      ` with a potential refund of ${formatCurrency(potentialRefund, subscription.currency || "USD")}`}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>

          {cancelType === "immediate" && (
            <FormGroup sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmUnderstanding}
                    onChange={(e) => setConfirmUnderstanding(e.target.checked)}
                  />
                }
                label="I understand that I will immediately lose access to all premium features"
              />
            </FormGroup>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Why are you cancelling?
        </Typography>

        {commonReasons.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {commonReasons.map((commonReason) => (
              <Button
                key={commonReason}
                size="small"
                variant="outlined"
                onClick={() => selectCommonReason(commonReason)}
                sx={{ borderRadius: "16px" }}
              >
                {commonReason}
              </Button>
            ))}
          </Box>
        )}

        <TextField
          margin="dense"
          label="Reason for cancellation"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={handleReasonChange}
          placeholder="Please tell us why you're cancelling"
          required
          error={submitting && !reason.trim()}
          helperText={submitting && !reason.trim() ? "Reason is required" : ""}
        />

        {cancelType === "immediate" && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Immediate cancellation will revoke access to premium features right away
            {subscription.billingCycle !== "free" &&
              potentialRefund <= 0 &&
              " with no refund for the remaining billing period"}
            .
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined">
          Keep Subscription
        </Button>
        <Button
          onClick={handleCancel}
          color="error"
          disabled={submitting || (cancelType === "immediate" && !confirmUnderstanding)}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {submitting ? "Processing..." : "Confirm Cancellation"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionCancelDialog

